// Copyright (c) 2025 UXtechLab.
// Originally created by @taekie. Released under the MIT License. See LICENSE for details.
/**
 * Voronoi Popup Utility
 *
 * Displays a popup/tooltip for clicked voronoi cells.
 * Handles positioning, template formatting, and cleanup.
 */

/**
 * Show a popup for a clicked voronoi cell
 *
 * @param {Object} clicked - The clicked cell data object
 * @param {Object} clicked.clickArea - D3 selection of the clicked path
 * @param {string} clicked.key - The key/label of the clicked cell
 * @param {Object} [clicked.data] - Additional data associated with the cell
 * @param {Object} [options] - Popup configuration options
 * @param {string} [options.format="{item}"] - Template string for popup content (e.g., "{key}: {value}")
 * @param {string} [options.popupId="vb-popup"] - DOM ID for the popup element
 * @param {string} [options.className="vb-popup"] - CSS class for the popup
 * @param {Function} [options.onClose] - Callback function when popup is closed
 * @returns {HTMLElement|undefined} The created popup element, or undefined if no popup was created
 */
export default function showVoronoiPopup(clicked, options = {}) {
  const {
    format = "{item}",
    popupId = "vb-popup",
    className = "vb-popup",
    onClose = null
  } = options;

  // Remove existing popup
  const existingPopup = document.getElementById(popupId);
  if (existingPopup) existingPopup.remove();

  // Exit if no clicked data
  if (!clicked || !clicked.clickArea) {
    if (onClose) onClose();
    return;
  }

  const clickedPath = clicked.clickArea.node();
  const svgElement = clickedPath.ownerSVGElement;
  if (!svgElement) return;

  // === Calculate position in page coordinates (unaffected by container zoom) ===
  // Get the path's bounding box in SVG coordinate space
  const pathBBox = clickedPath.getBBox();

  // Calculate cell center in SVG coordinates
  const svgCenterX = pathBBox.x + pathBBox.width / 2;
  const svgCenterY = pathBBox.y + pathBBox.height / 2;

  // Convert SVG coordinates to screen (page) coordinates
  // This handles all transformations including zoom, scale, translate
  // getBBox() is in the path's OWN user space, so the CTM must be the path's —
  // svgElement.getScreenCTM() misses every ancestor transform (margin translate, zoom).
  const svgPoint = svgElement.createSVGPoint();
  svgPoint.x = svgCenterX;
  svgPoint.y = svgCenterY;
  const screenPoint = svgPoint.matrixTransform(clickedPath.getScreenCTM());

  // Use screen coordinates directly (relative to viewport)
  const x = screenPoint.x + window.scrollX;
  const y = screenPoint.y + window.scrollY;

  // Space above the cell (popup placement is decided after measuring its height)
  const spaceAbove = screenPoint.y;

  // === Template substitution ===
  // Gather fields from every place the row may live: clicked's own top-level
  // (the library spreads the leaf's d.data here), clicked.data, and the d3 node's
  // depth-3 .data/.raw. Later spreads win, so the original row takes precedence.
  const node = clicked.d;
  const data = {
    ...clicked,
    key: clicked.key,
    ...(clicked.data || {}),
    ...(clicked.data?.data || {}),
    ...(node?.data?.data || {}),
    ...(node?.data?.raw?.[0] || {}),
    ...(node?.parent?.data?.data || {}),
    ...(node?.parent?.data?.raw?.[0] || {})
  };

  let content = format
    .replace(/\{(\w+)\}/g, (match, field) => {
      const val = data[field];
      return val !== undefined && val !== null ? String(val) : match;
    })
    .replace(/\\n/g, "<br>")
    .replace(/\n/g, "<br>");

  // === Create popup ===
  const popup = document.createElement("div");
  popup.id = popupId;
  popup.className = className;

  Object.assign(popup.style, {
    position: "absolute",
    left: "-9999px", // Render off-screen for size measurement
    top: "0px",
    zIndex: "1000"
  });

  popup.innerHTML = `<div class="vb-popup-content">
    <div class="vb-popup-message">${content}</div>
  </div>`;

  // Append to body (not container) to avoid zoom/transform effects
  document.body.appendChild(popup);

  // Measure size using offsetWidth/offsetHeight (synchronous)
  const popupWidth = popup.offsetWidth;
  const popupHeight = popup.offsetHeight;

  // Prefer placing the popup ABOVE the cell (conventional tooltip position).
  // Only drop below when it wouldn't fit above (would be clipped at the top).
  const gap = 10;
  const placeBelow = spaceAbove < popupHeight + gap;
  popup.classList.add(placeBelow ? "vb-popup-below" : "vb-popup-above");

  // Calculate horizontal position with boundary check
  let finalX = x - popupWidth / 2;
  const padding = 10; // Minimum padding from edges

  // Keep popup within viewport bounds (horizontal)
  if (finalX < padding) {
    finalX = padding;
  } else if (finalX + popupWidth > window.innerWidth - padding) {
    finalX = window.innerWidth - popupWidth - padding;
  }

  // Calculate vertical position
  const finalY = placeBelow ? y + 5 : y - 5 - popupHeight;

  // Set final position (fixed to page, not container)
  popup.style.left = `${finalX}px`;
  popup.style.top = `${finalY}px`;

  // === Outside click/touch handler (mobile-friendly) ===
  const handler = (e) => {
    if (!popup.contains(e.target) && !svgElement.contains(e.target)) {
      popup.remove();
      document.removeEventListener("click", handler);
      document.removeEventListener("touchstart", handler);
      const clickedCell = svgElement.querySelector("path.vb-clicked");
      if (clickedCell) clickedCell.classList.remove("vb-clicked");
      if (onClose) onClose();
    }
  };
  setTimeout(() => {
    document.addEventListener("click", handler);
    document.addEventListener("touchstart", handler); // Mobile support
  }, 10);

  return popup;
}
